import alertService from "../components/alert/alertService";
import apiClient from "../api/axios";
import { defaultCodeMap, handleApiError } from "../api/handleApiError";
import {
  GoalBaseYearPayload,
  GenerateGoalBaseline,
  GenerateGoalBaseYear,
  GetGoals,
  GetSubOrganizations,
  GetYearEmissions,
  GoalInfo,
  UpdateBaseYear,
  UpdateBaseline,
  GoalBaselinePayload,
} from "../models/GoalSetting";
import { GetDashboard } from "../models/GoalSettingDashboard";

export const goalSettingService = {
  getGoalSettingInfo: async (goalRefId: string): Promise<GoalInfo | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.get(
        `/v1/goal-setting/goal/${goalRefId}`
      );
      return response.data.result;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  getGoalSettingList: async (): Promise<GetGoals | null> => {
    try {
      const response = await apiClient.get("/v1/goal-setting/goal");
      return response.data.results as GetGoals;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  duplicateGoal: async (
    goalRefId: string,
    name: string
  ): Promise<GetGoals | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.post(`/v1/goal-setting/goal/duplicate`, {
        goalName: name,
        goalRefId: goalRefId,
      });
      return response.data.results as GetGoals;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  deleteGoals: async (goalRefIds: string[]): Promise<GetGoals | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.delete(`/v1/goal-setting/goal`, {
        data: { goalRefIds },
      });
      return response.data.results as GetGoals;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },

  getAvailableSuborganizationList:
    async (): Promise<GetSubOrganizations | null> => {
      try {
        const response = await apiClient.get(
          `/v1/goal-setting/goal/emission/available`
        );
        return response.data.results as GetSubOrganizations;
      } catch (error: any) {
        handleError(error);
      }
      return null;
    },
  getEmissionByYear: async (
    formData: any
  ): Promise<GetYearEmissions | null> => {
    try {
      const response = await apiClient.post(
        `/v1/goal-setting/goal/emission`,
        formData
      );
      return response.data.results as GetYearEmissions;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },

  generatePlanBaseyear: async (
    formData: any
  ): Promise<GenerateGoalBaseYear | null> => {
    try {
      const response = await apiClient.post(
        `/v1/goal-setting/goal/generate-plan/baseYear`,
        formData
      );
      return response.data.results as GenerateGoalBaseYear;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  generatePlanBaseline: async (
    formData: any
  ): Promise<GenerateGoalBaseline | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.post(
        `/v1/goal-setting/goal/generate-plan/baseline`,
        formData
      );
      return response.data.results as GenerateGoalBaseline;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },

  createGoalBaseYear: async (
    formData: GoalBaseYearPayload
  ): Promise<GetGoals | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.post(
        `/v1/goal-setting/goal/baseYear`,
        formData
      );
      return response.data.results as GetGoals;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  createGoalBaseline: async (
    formData: GoalBaselinePayload
  ): Promise<GetGoals | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.post(
        `/v1/goal-setting/goal/baseline`,
        formData
      );
      return response.data.results as GetGoals;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },

  updatedGoalBaseYear: async (
    goalRefId: string,
    formData: UpdateBaseYear
  ): Promise<GetGoals | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.patch(
        `/v1/goal-setting/goal/${goalRefId}`,
        formData
      );
      return response.data.results as GetGoals;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  updatedGoalBaseline: async (
    goalRefId: string,
    formData: UpdateBaseline
  ): Promise<GetGoals | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.patch(
        `/v1/goal-setting/goal/${goalRefId}`,
        formData
      );
      return response.data.results as GetGoals;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },

  getDashboard: async (): Promise<GetDashboard | null> => {
    try {
      const response = await apiClient.get(`/v1/goal-setting/goal/dashboard`);
      return response.data.results as GetDashboard;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
};

const handleError = (error: unknown) =>
  handleApiError(error, {
    VALIDATION_ERROR: {
      messageKey: (body) =>
        body.includes("Duplicate") ? "Component-Name-Duplicated" : "validation-failed",
      type: "error",
    },
    FORBIDDEN_ACTION_ERROR: "Forbidden-Action-Error",
    FORBIDDEN_GOAL_SETTING_ERROR: defaultCodeMap.FORBIDDEN_ACTION_ERROR,
    // another goal for this organization is mid-save
    TOO_MANY_REQUESTS: { messageKey: "Goal-Save-Busy", type: "info" },
    GOAL_NOT_FOUND: "Goal-Not-Found",
    GOAL_ALREADY_EXISTS: "Goal-Already-Exists",
    GOAL_NOT_IN_ORGANIZATION: "Goal-Not-In-Organization",
    GOAL_CREATION_PROCESS_FAILED: "Goal-Creation-Process-Failed",
    GOAL_RETRIEVAL_PROCESS_FAILED: "Goal-Retrieval-Process-Failed",
    GOAL_UPDATE_PROCESS_FAILED: "Goal-Update-Process-Failed",
    GOAL_DELETE_PROCESS_FAILED: "Goal-Delete-Process-Failed",
    GOAL_DUPLICATE_PROCESS_FAILED: "Goal-Duplicate-Process-Failed",
    GOAL_PLAN_GENERATE_PROCESS_FAILED: "Goal-Plan-Generate-Process-Failed",
    GOAL_RETRIEVE_DASHBOARD_PROCESS_FAILED: "Goal-Retrieve-Dashboard-Process-Failed",
  });
